import React, { useState } from 'react';

import copyTokens from '@amzn/meridian-tokens/base/icon/copy';
import shareTokens from '@amzn/meridian-tokens/base/icon/share';
import undoTokens from '@amzn/meridian-tokens/base/icon/undo';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import ReactMarkdown from 'react-markdown';
import { useHistory } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { ROUTES } from 'src/constants/routes';

import { Artifact } from '../types';
import { parseArtifactContent } from '../utils';
import { hasConsistentSchema } from '../utils/helpers';
import './AIChatArtifact.scss';
import { JsonDisplay } from './JsonDisplay';
import { JsonTableDisplay } from './JsonTableDisplay';

interface AIChatArtifactProps {
    artifacts: Artifact[];
    isLoading: boolean;
    conversationId: string;
    timestamp: string;
    isSharedView?: boolean;
    error?: string | null;
}

export const AIChatArtifact: React.FC<AIChatArtifactProps> = ({
    artifacts,
    isLoading,
    conversationId,
    timestamp,
    isSharedView = false,
    error
}) => {
    const [copyShareLinkText, setCopyShareLinkText] = useState('');
    const history = useHistory();

    const handleShareArtifacts = async () => {
        if (!conversationId || !timestamp) return;

        const shareUrl = `${window.location.origin}${ROUTES.AI.INDEX}/${conversationId}/${timestamp}`;

        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopyShareLinkText('Copied!');
            setTimeout(() => setCopyShareLinkText(''), 2000);
        } catch (err) {
            console.error('Failed to copy share URL: ', err);
            setCopyShareLinkText('Failed');
            setTimeout(() => setCopyShareLinkText(''), 2000);
        }
    };

    const handleBackToChat = () => {
        history.push(`${ROUTES.AI.INDEX}/${conversationId}`);
    };

    const renderSharedViewHeader = () => (
        <Column spacing="400">
            <Row alignmentVertical="center" spacing="200">
                <Heading level={3}>View Artifacts</Heading>
                <Tooltip position={'end'} title={'Back to Chat'}>
                    <Row spacing="none">
                        <Button size="small" onClick={handleBackToChat}>
                            <Icon tokens={undoTokens} />
                        </Button>
                        <Text type="b200" color="secondary">
                            {copyShareLinkText}
                        </Text>
                    </Row>
                </Tooltip>
            </Row>
        </Column>
    );

    if (isLoading) {
        return (
            <Column>
                <Loader size="medium" />
                <Text>Loading artifacts...</Text>
            </Column>
        );
    }

    if (error) {
        return (
            <Column>
                <Text color="error">{error}</Text>
            </Column>
        );
    }

    if (!artifacts || artifacts.length === 0) {
        return (
            <Column spacingInset="none 400">
                {isSharedView && renderSharedViewHeader()}
                <Text>No artifacts available</Text>
            </Column>
        );
    }

    const artifactContent = (
        <Column spacing="200">
            <Row alignmentVertical="center" spacing="none 200">
                <Text color="secondary">
                    {artifacts.length} artifact{artifacts.length !== 1 ? 's' : ''} found
                </Text>
                {conversationId && timestamp && !isSharedView && (
                    <Tooltip position={'end'} title={'Copy Artifact Share Link'}>
                        <Row spacing="0">
                            <Button
                                type="link"
                                size="small"
                                onClick={handleShareArtifacts}
                                disabled={copyShareLinkText !== ''}
                            >
                                <Icon tokens={shareTokens} />
                            </Button>
                            <Text type="b200" color="secondary">
                                {copyShareLinkText}
                            </Text>
                        </Row>
                    </Tooltip>
                )}
            </Row>

            <Column spacing="400">
                {artifacts.map((artifact, index) => (
                    <ArtifactItem
                        key={`${artifact.objectKey}-${index}`}
                        artifact={artifact}
                        index={index}
                        isSharedView={isSharedView}
                    />
                ))}
            </Column>
        </Column>
    );

    if (isSharedView) {
        return (
            <Column spacingInset="none 400" width="100%">
                {renderSharedViewHeader()}
                {artifactContent}
            </Column>
        );
    }

    return artifactContent;
};

interface ArtifactItemProps {
    artifact: Artifact;
    index: number;
    isSharedView?: boolean;
}

const ArtifactItem: React.FC<ArtifactItemProps> = ({ artifact, index, isSharedView = false }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const parsedContent = parseArtifactContent(artifact);
    const hasLink = parsedContent.link;
    const text = parsedContent.text || artifact.objectContent;

    const shouldTruncate = (text: string, maxLength = 500): boolean => {
        return text.length > maxLength;
    };

    const truncateText = (text: string, maxLength = 500): string => {
        if (!shouldTruncate(text, maxLength)) return text;
        return text.substring(0, maxLength) + '...';
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    // Helper function to check if content can be parsed as JSON
    const canParseAsJson = (content: string): boolean => {
        try {
            JSON.parse(content);
            return true;
        } catch {
            return false;
        }
    };

    // Helper function to check if parsed JSON is a valid array for table display
    const canDisplayAsTable = (content: string): boolean => {
        try {
            const data = JSON.parse(content);

            // Handle array of JSON strings (double-encoded)
            if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
                try {
                    const parsedObjects = data.map((item) => JSON.parse(item));
                    return hasConsistentSchema(parsedObjects);
                } catch {
                    return false;
                }
            }

            // Handle regular array of objects
            return Array.isArray(data) && hasConsistentSchema(data);
        } catch {
            return false;
        }
    };

    // Helper function to determine if "Show full artifact" button should be displayed
    const shouldShowFullArtifactButton = (): boolean => {
        const isTruncated = shouldTruncate(text);

        // For json_table in shared view, if we're already showing the table, no need for button
        if (isSharedView && ['json_table', 'table'].includes(artifact.artifactType) && canDisplayAsTable(text)) {
            return false;
        }

        // For all other cases, show button if content is truncated
        return isTruncated;
    };

    // Render content in the card (potentially truncated)
    const renderCardContent = () => {
        const isTruncated = shouldTruncate(text);

        // For json_table type
        if (['json_table', 'table'].includes(artifact.artifactType)) {
            // In shared view, display table directly if it's valid for table display
            if (isSharedView && canDisplayAsTable(text)) {
                try {
                    const data = JSON.parse(text);

                    // Handle array of JSON strings (double-encoded)
                    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
                        const parsedObjects = data.map((item) => JSON.parse(item));
                        return <JsonTableDisplay data={parsedObjects} />;
                    }

                    return <JsonTableDisplay data={data} />;
                } catch {
                    // Fallback to JsonDisplay
                    return <JsonDisplay data={text} />;
                }
            }

            // In regular view, only show JSON if not truncated
            if (!isTruncated && canParseAsJson(text)) {
                return <JsonDisplay data={text} />;
            }
            // If truncated, show truncated text and let modal handle the table
            return <Text>{truncateText(text)}</Text>;
        }

        // For unknown type
        if (artifact.artifactType === 'unknown') {
            if (!isTruncated && canParseAsJson(text)) {
                return <JsonDisplay data={text} />;
            }
            // If truncated or not JSON, show as text
            return <Text>{truncateText(text)}</Text>;
        }

        // For other JSON types
        if (['api_response', 'json', 'application/json'].includes(artifact.artifactType)) {
            if (!isTruncated) {
                return <JsonDisplay data={text} />;
            }
            return <Text>{truncateText(text)}</Text>;
        }

        // Default: show as text
        return <Text>{truncateText(text)}</Text>;
    };

    // Render full content in modal
    const renderFullArtifact = () => {
        // For json_table type - prefer table display in modal
        if (['json_table', 'table'].includes(artifact.artifactType)) {
            if (canDisplayAsTable(text)) {
                try {
                    const data = JSON.parse(text);

                    // Handle array of JSON strings (double-encoded)
                    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
                        const parsedObjects = data.map((item) => JSON.parse(item));
                        return <JsonTableDisplay data={parsedObjects} />;
                    }

                    return <JsonTableDisplay data={data} />;
                } catch {
                    // Fallback to JsonDisplay
                    return <JsonDisplay data={text} />;
                }
            }
            // If not valid for table, try JSON display
            if (canParseAsJson(text)) {
                return <JsonDisplay data={text} />;
            }
            // Final fallback to markdown
            return (
                <ReactMarkdown className="markdown-body" remarkPlugins={[remarkGfm]}>
                    {text}
                </ReactMarkdown>
            );
        }

        // For JSON types (including unknown if it's JSON)
        if (
            ['api_response', 'json', 'application/json'].includes(artifact.artifactType) ||
            (artifact.artifactType === 'unknown' && canParseAsJson(text))
        ) {
            return <JsonDisplay data={text} />;
        }

        return (
            <ReactMarkdown className="markdown-body" remarkPlugins={[remarkGfm]}>
                {text}
            </ReactMarkdown>
        );
    };

    return (
        <>
            <Card spacingInset="400">
                <Column spacingInset="200 none">
                    <Row alignmentVertical="center" spacing="200">
                        <Text type="b300">Artifact {index + 1}</Text>
                        <Box type="fill" className="artifact-type">
                            <Text type="b200" color="inverted">
                                {artifact.artifactType}
                            </Text>
                        </Box>
                    </Row>
                </Column>

                {hasLink && (
                    <Row alignmentVertical="center" spacing="200">
                        <Text type="b100" className="artifact-source-text">
                            Source: {parsedContent.link}
                        </Text>
                        <Button type="link" size="small" onClick={() => copyToClipboard(parsedContent.link)}>
                            <Icon tokens={copyTokens} />
                        </Button>
                    </Row>
                )}

                <Column maxHeight="500px">
                    {renderCardContent()}
                    {shouldShowFullArtifactButton() && (
                        <Button type="link" size="small" onClick={() => setModalOpen(true)}>
                            Show full artifact
                        </Button>
                    )}
                </Column>
            </Card>

            <Modal open={modalOpen} onClose={setModalOpen} scrollContainer={'modal'} title={'View Artifact'}>
                {renderFullArtifact()}
            </Modal>
        </>
    );
};
